import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Battery, Gauge, Scale, Shield, Zap, Clock, Download, ArrowRight, Building2, User, MessageSquare, ChevronLeft, ChevronRight,
  MapPin, Users
} from 'lucide-react';
import { supabase, safeSupabaseOperation } from '../../lib/supabase';
import type { Product, Location } from '../../types/database';

interface Banner {
  id: string;
  type: 'moto' | 'station' | 'battery';
  title: string;
  subtitle: string;
  gradient: string;
  pattern: string;
  data?: Product | Location;
  features: Array<{
    icon: React.ReactNode;
    label: string;
    value: string;
  }>;
  cta: {
    primary: {
      label: string;
      link: string;
    };
    secondary?: {
      label: string;
      link: string;
    };
  };
}

const isSupabaseAvailable = async (): Promise<boolean> => {
  try {
    if (!supabase) return false;
    const { error } = await supabase.from('products').select('id').limit(1);
    return !error;
  } catch {
    return false;
  }
};

const Hero: React.FC = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [animating, setAnimating] = useState(false);
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (banners.length > 0) {
        nextBanner();
      }
    }, 6000);
    
    return () => clearInterval(interval);
  }, [banners, currentIndex]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Product slides for carousel
      const defaultBanners: Banner[] = [
        {
          id: 'sunu',
          type: 'moto',
          title: 'Sunu',
          subtitle: 'L\'Excellence pour les Professionnels',
          gradient: 'from-primary-600 via-primary-500 to-primary-700',
          pattern: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 2%, transparent 2.5%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.2) 2%, transparent 2.5%)',
          features: [
            { icon: <Battery size={16} />, label: 'Autonomie', value: '90 - 100 km' },
            { icon: <Gauge size={16} />, label: 'Vitesse Max', value: '80 km/h' },
            { icon: <Scale size={16} />, label: 'Poids', value: '275 kg' }
          ],
          cta: {
            primary: {
              label: 'Découvrir Sunu',
              link: '/products'
            },
            secondary: {
              label: 'Réserver un essai',
              link: '/contact'
            }
          }
        },
        {
          id: 'terra',
          type: 'moto',
          title: 'Terra',
          subtitle: 'Puissance et robustesse pour tous les terrains',
          gradient: 'from-accent-400 via-accent-300 to-accent-500',
          pattern: 'linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.1) 25%, transparent 25%)',
          features: [
            { icon: <Battery size={16} />, label: 'Autonomie', value: '120 km' },
            { icon: <Gauge size={16} />, label: 'Vitesse Max', value: '45 km/h' },
            { icon: <Scale size={16} />, label: 'Poids', value: '95 kg' }
          ],
          cta: {
            primary: {
              label: 'Découvrir Terra',
              link: '/products'
            },
            secondary: {
              label: 'Configurer',
              link: '/products/terra'
            }
          }
        },
        {
          id: 'teranga',
          type: 'moto',
          title: 'Teranga',
          subtitle: 'Accessible et fiable pour tous vos déplacements',
          gradient: 'from-primary-700 via-primary-600 to-primary-800',
          pattern: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.1), rgba(255,255,255,0.1) 1px, transparent 1px, transparent 20px)',
          features: [
            { icon: <Battery size={16} />, label: 'Autonomie', value: '100 km' },
            { icon: <Gauge size={16} />, label: 'Vitesse Max', value: '45 km/h' },
            { icon: <Scale size={16} />, label: 'Poids', value: '85 kg' }
          ],
          cta: {
            primary: {
              label: 'Découvrir Teranga',
              link: '/products/teranga'
            },
            secondary: {
              label: 'Essai gratuit',
              link: '/contact'
            }
          }
        },
        {
          id: 'teranga-plus',
          type: 'moto',
          title: 'Teranga+',
          subtitle: 'Le Nouveau Pour Particuliers - Phase Pilote 2025',
          gradient: 'from-accent-500 via-accent-400 to-primary-500',
          pattern: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.15) 2%, transparent 2.5%), radial-gradient(circle at 25% 75%, rgba(255,255,255,0.15) 2%, transparent 2.5%)',
          features: [
            { icon: <Battery size={16} />, label: 'Autonomie', value: '110 km' },
            { icon: <Gauge size={16} />, label: 'Vitesse Max', value: '50 km/h' },
            { icon: <Scale size={16} />, label: 'Poids', value: '88 kg' }
          ],
          cta: {
            primary: {
              label: 'Précommander',
              link: '/contact'
            },
            secondary: {
              label: 'En savoir plus',
              link: '/products/teranga-plus'
            }
          }
        },
        {
          id: 'omega',
          type: 'moto',
          title: 'Omega',
          subtitle: 'TukTuk électrique - Transport urbain polyvalent',
          gradient: 'from-primary-500 via-accent-400 to-primary-600',
          pattern: 'linear-gradient(135deg, rgba(255,255,255,0.1) 25%, transparent 25%), linear-gradient(-135deg, rgba(255,255,255,0.1) 25%, transparent 25%)',
          features: [
            { icon: <Battery size={16} />, label: 'Autonomie', value: '120 km' },
            { icon: <Gauge size={16} />, label: 'Vitesse Max', value: '45 km/h' },
            { icon: <Users size={16} />, label: 'Capacité', value: '3-4 passagers' }
          ],
          cta: {
            primary: {
              label: 'Découvrir Omega',
              link: '/products/omega-tuktuk'
            },
            secondary: {
              label: 'Demander un devis',
              link: '/contact'
            }
          }
        }
      ];

      // Set default banners (static slides, no Supabase data needed)
      setBanners(defaultBanners);
      setIsOffline(false);
    } catch (error) {
      console.warn('Error initializing Hero component:', error);
      setIsOffline(true);
      // Component will continue to work with default banners
    } finally {
      setLoading(false);
    }
  };

  const nextBanner = () => {
    if (banners.length > 0 && !animating) {
      setAnimating(true);
      setCurrentIndex((prev) => (prev + 1) % banners.length);
      setTimeout(() => setAnimating(false), 500);
    }
  };

  const prevBanner = () => {
    if (banners.length > 0 && !animating) {
      setAnimating(true);
      setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
      setTimeout(() => setAnimating(false), 500);
    }
  };

  const currentBanner = banners[currentIndex];

  const renderBannerContent = (banner: Banner) => {
    return (
      <div className="relative bg-gradient-to-br from-white/95 to-white/90 backdrop-blur-lg rounded-3xl p-6 sm:p-8 shadow-2xl border border-white/50 transform transition-all duration-500">
        <div className="relative">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-500 px-4 py-2 rounded-full text-white text-xs sm:text-sm font-medium mb-3 shadow-lg">
            <Zap size={14} className="animate-pulse" />
            {banner.id === 'teranga-plus' ? 'Phase Pilote 2025' : 'Nouvelle Génération'}
          </div>

          {/* Title */}
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 bg-gradient-to-r from-gray-900 via-gray-800 to-primary-900 bg-clip-text text-transparent">
            {banner.title}
          </h2>

          <p className="text-xs sm:text-sm text-gray-600 mb-4">{banner.subtitle}</p>

          {/* Features */}
          <div className="flex flex-wrap gap-3 mb-6">
            {banner.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 text-gray-700">
                <div className="p-1.5 bg-primary-50 rounded-lg text-primary-600">
                  {feature.icon}
                </div>
                <div>
                  <div className="text-xs text-gray-500">{feature.label}</div>
                  <div className="text-xs sm:text-sm font-semibold text-gray-900">{feature.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Link
              to={banner.cta.primary.link}
              className="inline-flex items-center justify-center bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white px-4 sm:px-6 py-2.5 rounded-xl font-medium transition-all shadow-lg hover:shadow-xl text-xs sm:text-sm group"
            >
              {banner.cta.primary.label}
              <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
            {banner.cta.secondary && (
              <Link
                to={banner.cta.secondary.link}
                className="inline-flex items-center justify-center bg-white hover:bg-gray-50 text-primary-700 px-4 sm:px-6 py-2.5 rounded-xl font-medium transition-all border border-primary-200 hover:border-primary-300 text-xs sm:text-sm"
              >
                {banner.cta.secondary.label}
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Show loading state only if we don't have any banners yet
  if (loading && banners.length === 0) {
    return (
      <section className="relative min-h-[85vh] flex items-center pt-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-800 to-primary-700 z-0"></div>
        <div className="container relative mx-auto px-4 md:px-6 py-8 sm:py-12 md:py-20 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* LEFT - Visual Loading */}
            <div className="flex items-center justify-center lg:justify-start order-2 lg:order-1">
              <div className="animate-pulse bg-white/10 rounded-2xl w-full max-w-2xl h-64"></div>
            </div>
            {/* RIGHT - Carousel Loading */}
            <div className="flex items-center justify-center order-1 lg:order-2">
              <div className="animate-pulse bg-white/10 rounded-3xl p-8 w-full max-w-md h-64"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-h-[85vh] flex items-center pt-16 overflow-hidden">
      {/* Background with gradient and pattern */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-800 to-primary-700 z-0"></div>
      {currentBanner && (
        <div
          className="absolute inset-0 z-0 transition-opacity duration-1000"
          style={{
            background: `linear-gradient(to right, ${currentBanner.gradient})`,
            backgroundSize: '200% 200%',
            animation: 'gradientFlow 15s ease infinite'
          }}
        >
          <div
            className="absolute inset-0"
            style={{
              background: currentBanner.pattern,
              backgroundSize: '30px 30px',
              opacity: 0.1
            }}
          />
        </div>
      )}

      <style>
        {`
          @keyframes gradientFlow {
            0% { background-position: 0% 50% }
            50% { background-position: 100% 50% }
            100% { background-position: 0% 50% }
          }

          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }

          .floating {
            animation: float 6s ease-in-out infinite;
          }
        `}
      </style>

      <div className="container relative mx-auto px-4 md:px-6 py-8 sm:py-12 md:py-20 z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* LEFT SIDE - Fixed Premium Visual */}
          <div className="flex items-center justify-center lg:justify-start order-2 lg:order-1">
            <div className="w-full max-w-2xl">
              <img
                src="/slide_proxy_emobility_opti(1).png"
                alt="Écosystème Proxy Emobility"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>

          {/* RIGHT SIDE - Product Carousel */}
          {currentBanner && (
            <div className="order-1 lg:order-2">
              <div className={`transition-all duration-500 ${animating ? 'opacity-0 transform translate-x-10' : 'opacity-100 transform translate-x-0'}`}>
                <div className="floating relative">
                  {renderBannerContent(currentBanner)}

                  {/* Navigation Buttons */}
                  <button
                    onClick={prevBanner}
                    className="absolute top-1/2 -left-2 sm:-left-6 transform -translate-y-1/2 p-2 sm:p-3 rounded-full bg-white/80 hover:bg-white shadow-md transition-colors group z-20"
                    aria-label="Bannière précédente"
                  >
                    <ChevronLeft size={20} className="text-gray-900 transition-transform group-hover:-translate-x-1" />
                  </button>
                  <button
                    onClick={nextBanner}
                    className="absolute top-1/2 -right-2 sm:-right-6 transform -translate-y-1/2 p-2 sm:p-3 rounded-full bg-white/80 hover:bg-white shadow-md transition-colors group z-20"
                    aria-label="Bannière suivante"
                  >
                    <ChevronRight size={20} className="text-gray-900 transition-transform group-hover:translate-x-1" />
                  </button>

                  {/* Slide Indicators */}
                  <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
                    {banners.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          index === currentIndex
                            ? 'w-6 sm:w-8 bg-white'
                            : 'w-2 bg-white/50 hover:bg-white/70'
                        }`}
                        aria-label={`Aller à la bannière ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;